import { Banner, LoadingWand } from '@ordr-as/umami-react'
import { useEffect, useState } from 'react'
import TimeIllustration from '../../../assets/time-illustration.png'
import { useRequest } from '../../../api/utils'
import { formatDateAndTime } from '../../../utils/stringUtils'
import PaginationButtons from '../../../components/PaginationButtons'
import { useSearchTerminalEvents } from '../../../api/terminalApi'
import { useSearchParams } from 'react-router-dom'
import { Page } from '../../../api/pagination/page'
import { TerminalEventDto } from '../../../../generated-api'
import Payload from 'components/Payload/Payload'

const toIntOrZero = (maybeInt: string | null) => {
    if (maybeInt === null) return 0
    const parsed = parseInt(maybeInt)
    return isNaN(parsed) ? 0 : parsed
}

const TerminalEventsTab = (props: { terminalExternalId: string | undefined }) => {
    const [terminalEvents, setTerminalEvents] = useState<Page<TerminalEventDto>>()
    const [selectedEvent, setSelectedEvent] = useState<TerminalEventDto>()

    const [searchParams, setSearchParams] = useSearchParams()
    const searchTerminalEvents = useSearchTerminalEvents()

    const pageNumber = toIntOrZero(searchParams.get('page'))

    const {
        run: runGetTerminalEvents,
        loading,
        error,
    } = useRequest(() => searchTerminalEvents(props.terminalExternalId, pageNumber))

    useEffect(() => {
        runGetTerminalEvents().then((terminalEventsPage) => {
            setTerminalEvents(terminalEventsPage)
        })

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageNumber])

    const setPageNumber = (pageNumber: number) => {
        setSearchParams({ page: pageNumber.toString() })
    }

    return (
        <div className="flex flex-col desktop:flex-row rounded-lg rounded-tl-none bg-brand-white shadow-lg py-4">
            {loading ? (
                <div className="text-center flex my-6 mx-auto">
                    <LoadingWand className="mx-auto" />
                </div>
            ) : error ? (
                <Banner kind="error" onClose={() => null} stretch="parent" className="my-3">
                    {error?.message}
                </Banner>
            ) : terminalEvents && terminalEvents.numberOfElements > 0 ? (
                <>
                    <section className="flex-1 desktop:w-6/12 desktop:order-last border-b-2 desktop:border-0 border-brand-primary desktop:border-grey-lightest pb-3 desktop:pb-0">
                        {selectedEvent ? (
                            <Payload event={selectedEvent} />
                        ) : (
                            <div className="h-full flex flex-col items-center justify-center">
                                <img width={180} height={180} src={TimeIllustration} alt="" />
                                <p className="small text-grey-dark">Select event for payload</p>
                            </div>
                        )}
                    </section>
                    <section className="flex-1 desktop:border-r border-grey-lightest desktop:w-6/12 pt-3 desktop:pt-0 overflow-x-auto">
                        <div className="min-w-full">
                            <table className="text-left overflow-hidden w-full">
                                <thead className="text-grey-dark">
                                    <tr>
                                        <th scope="col" className="font-normal py-2 pl-6">
                                            Event code
                                        </th>
                                        <th scope="col" className="font-normal py-2 px-2">
                                            New state
                                        </th>
                                        <th scope="col" className="font-normal py-2 pr-2">
                                            Received from PSP
                                        </th>
                                        <th scope="col" className="font-normal py-2 pr-2">
                                            PSP event timestamp
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {((terminalEvents?.pageContent || []) as TerminalEventDto[]).map((event, idx) => (
                                        <tr
                                            onClick={() => setSelectedEvent(event)}
                                            key={idx}
                                            className={`hover:bg-purple-lightest cursor-pointer border-t border-grey-lightest ${
                                                event === selectedEvent && 'bg-purple-lightest'
                                            }`}
                                        >
                                            <th
                                                scope="row"
                                                className="text-brand-primary py-4 pl-6 whitespace-nowrap text-sm"
                                            >
                                                {event.eventCode}
                                            </th>
                                            <td className="py-4 whitespace-nowrap text-sm px-2">{event.newState}</td>
                                            <td className="py-4 whitespace-nowrap text-sm pr-2">
                                                {formatDateAndTime(event.receivedFromPsp?.toString() ?? '')}
                                            </td>
                                            <td className="py-4 whitespace-nowrap text-sm pr-2">
                                                {event.pspEventTimestamp
                                                    ? formatDateAndTime(event.pspEventTimestamp?.toString() ?? '')
                                                    : ''}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <PaginationButtons page={terminalEvents} onSwapPage={setPageNumber} />
                    </section>
                </>
            ) : (
                <Banner kind="warn" stretch="parent" onClose={() => null}>
                    {error ?? 'No terminal events found'}
                </Banner>
            )}
        </div>
    )
}

export default TerminalEventsTab
